import {
  type GitHubConnection,
  useSessionDismissal,
} from '@vivicta/github-connection';
import { Button, TONE_DOT_CLASS, cn } from '@vivicta/ui';
import { X } from 'lucide-react';

const DISMISS_KEY = 'vivicta.topBar.githubChipDismissed';

/**
 * Says in the shell that GitHub is not connected, so users don't find out one
 * page at a time. Hidden once connected, or for the session once dismissed —
 * the account menu keeps the way back.
 */
export const GitHubStatusChip = ({
  connection,
}: {
  connection: GitHubConnection;
}) => {
  const { dismissed, dismiss } = useSessionDismissal(DISMISS_KEY);

  if (connection.state !== 'none' || dismissed) {
    return null;
  }

  return (
    <div className="hidden shrink-0 items-center rounded-full border border-border bg-background sm:flex">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 gap-2 rounded-full rounded-r-none pl-3 pr-2 text-xs"
        disabled={connection.connecting}
        title={connection.error || undefined}
        onClick={() => void connection.connect()}
      >
        <span
          aria-hidden="true"
          className={cn('size-2 shrink-0 rounded-full', TONE_DOT_CLASS.warn)}
        />
        {connection.connecting ? 'Connecting…' : 'GitHub not connected'}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Hide GitHub status for this session"
        className="size-8 rounded-full rounded-l-none text-muted-foreground"
        onClick={dismiss}
      >
        <X aria-hidden="true" className="size-3.5" />
      </Button>
    </div>
  );
};
